import {faFile} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Flex, Input, Modal, Space, Tooltip} from 'antd';
import {ReactElement, useMemo, useState} from 'react';

import {useLocale} from '../../../../../../../../provider/locale/locale-hook';
import {LocalePlural} from '../../../../../../../../provider/locale/locale-plural';
import {Locale} from '../../../../../../../../provider/locale/localization';
import {useModal} from '../../../../../../../../provider/modal/modal-hook';
import {useSnackbar} from '../../../../../../../../provider/snackbar/snackbar-hook';
import {useSystem} from '../../../../../../../../provider/system/system-hook';
import {fetchReviewsReply} from '../../../../../../../../service/reviews/reviews-reply';
import {ResponseTemplateDataType} from '../../../../../../../../service/reviews/reviews-response-templates';
import {IdNumberType} from '../../../../../../../../util/type';
import {AutoReplyTemplatePopup} from '../../../../../../auto-reply-form/auto-reply-form-popup/auto-reply-template-popup';
import {isReviewReplyAllowed} from '../../../../../reviews-helper';
import {ReviewDataWithCatalogType} from '../../../review-list-type';
import {processBatch} from '../../review-helper';

import * as styles from './bulk-answer-modal.scss';

const {TextArea} = Input;

type PropsType = {
    onCancel: () => void;
    onSuccess?: () => void;
    refreshReview: (reviewId: number) => Promise<unknown>;
    selectedReviews: Array<ReviewDataWithCatalogType>;
};

export function BulkAnswerModal(props: PropsType): ReactElement {
    const {onCancel, onSuccess, selectedReviews, refreshReview} = props;
    const [answerText, setAnswerText] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTemplateList, setSelectedTemplateList] = useState<Array<IdNumberType>>([]);
    const [isSelectTemplatePopupOpen, setIsSelectTemplatePopupOpen] = useState<boolean>(false);
    const [selectedResponseTemplateList, setSelectedResponseTemplateList] = useState<Array<ResponseTemplateDataType>>(
        []
    );
    const {snackbar} = useSnackbar();
    const {getLocalizedString} = useLocale();
    const {modal} = useModal();
    const {screen} = useSystem();

    const answerableReviews = useMemo(
        () => selectedReviews.filter((review) => isReviewReplyAllowed(review)),
        [selectedReviews]
    );

    const canAnswerCount = answerableReviews.length;
    const totalCount = selectedReviews.length;
    const canAnswerAll = canAnswerCount === totalCount;
    const canAnswerNone = canAnswerCount === 0;

    const templatePickerReviewId = answerableReviews[0]?.id ?? selectedReviews[0]?.id;

    async function submitAnswers() {
        setIsLoading(true);

        try {
            // Process answers in batches
            await processBatch(answerableReviews, 50, async (review: ReviewDataWithCatalogType) => {
                await fetchReviewsReply(review.id, {
                    comment: answerText.trim(),
                });
            });

            // Refresh all reviews to show updated data in batches
            await processBatch(answerableReviews, 50, async (review: ReviewDataWithCatalogType) => {
                await refreshReview(review.id);
            });

            snackbar.success(<Locale stringKey="REVIEWS__REVIEW_ANSWER_SEND_SUCCESS__TITLE" />);

            if (onSuccess) {
                onSuccess();
            } else {
                onCancel();
            }
        } catch {
            snackbar.error(<Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />);
        } finally {
            setIsLoading(false);
        }
    }

    async function handleOk() {
        if (!answerText.trim()) {
            return;
        }

        if (!canAnswerAll) {
            modal.confirm({
                maskClosable: true,
                title: <Locale stringKey="REVIEWS__BULK_ANSWER__CONFIRM_MODAL_TITLE" />,
                content: (
                    <div>
                        <LocalePlural
                            count={totalCount}
                            fewKey="REVIEWS__BULK_ANSWER__CONFIRM_CONTENT__FEW"
                            manyKey="REVIEWS__BULK_ANSWER__CONFIRM_CONTENT__MANY"
                            singularKey="REVIEWS__BULK_ANSWER__CONFIRM_CONTENT__SINGULAR"
                            valueMap={{
                                canAnswerCount: String(canAnswerCount),
                                totalCount: String(totalCount),
                            }}
                        />
                    </div>
                ),
                okText: <Locale stringKey="REVIEWS__BULK_ANSWER__CONFIRM_OK" />,
                cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
                onOk: async () => {
                    await submitAnswers();
                },
            });

            return;
        }

        await submitAnswers();
    }

    return (
        <>
            <Modal
                classNames={{
                    body: styles.BulkAnswerModal_body,
                    content: styles.BulkAnswerModal_content,
                }}
                footer={
                    <Flex justify="space-between" wrap="wrap">
                        <Button disabled={canAnswerNone} onClick={() => setIsSelectTemplatePopupOpen(true)}>
                            <FontAwesomeIcon icon={faFile} />
                            {!screen.isMobile && <Locale stringKey="REVIEWS__BULK_ANSWER__TEMPLATE__BUTTON" />}
                        </Button>
                        <Space wrap>
                            <Button onClick={onCancel}>
                                <Locale stringKey="POPUP__BUTTON__CANCEL" />
                            </Button>
                            <Tooltip
                                title={
                                    canAnswerNone ? <Locale stringKey="REVIEWS__BULK_ANSWER__DISABLED_TOOLTIP" /> : null
                                }
                            >
                                <Button
                                    disabled={!answerText.trim() || canAnswerNone}
                                    loading={isLoading}
                                    onClick={handleOk}
                                    type="primary"
                                >
                                    <Locale stringKey="TEXT__APPLY" />
                                </Button>
                            </Tooltip>
                        </Space>
                    </Flex>
                }
                onCancel={onCancel}
                open
                title={<Locale stringKey="REVIEWS__BULK_ANSWER__TITLE" />}
                zIndex={1071}
            >
                <div className={styles.BulkAnswerModal_textAreaWrap}>
                    <TextArea
                        className={styles.BulkAnswerModal_textArea}
                        classNames={{textarea: styles.BulkAnswerModal_textAreaField}}
                        onChange={(event) => setAnswerText(event.target.value)}
                        placeholder={getLocalizedString('REVIEWS__BULK_ANSWER__PLACEHOLDER')}
                        value={answerText}
                    />
                </div>
            </Modal>
            {isSelectTemplatePopupOpen && (
                <AutoReplyTemplatePopup
                    isOpen
                    isRadioMode
                    onCancel={() => setIsSelectTemplatePopupOpen(false)}
                    onOk={() => {
                        setIsSelectTemplatePopupOpen(false);

                        const [selectedAutoReply] = selectedResponseTemplateList;

                        if (!selectedAutoReply) {
                            return;
                        }

                        const {message} = selectedAutoReply;

                        setAnswerText(message);
                    }}
                    reviewId={templatePickerReviewId}
                    selectedItemList={selectedTemplateList}
                    selectedResponseTemplateList={selectedResponseTemplateList}
                    setSelectedItemList={setSelectedTemplateList}
                    setSelectedResponseTemplateList={setSelectedResponseTemplateList}
                    shouldRememberGroupFilter
                    zIndex={1072}
                />
            )}
        </>
    );
}
